import { useEffect, useRef } from 'react';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { HelpCircle } from 'lucide-react';

const TOUR_SEEN_KEY = 'la360_dashboard_tour_v1';

// Steps reference elements by their existing data-testid. Steps whose element
// is not on the page at runtime are skipped automatically.
const STEP_DEFS = [
  {
    popover: {
      title: 'Welcome to your dashboard 👋',
      description: "Here's a quick 60-second tour of what you'll find. You can leave the tour anytime using the ✕ in the corner (or press Esc).",
    },
  },
  {
    popover: {
      title: 'First: choose a plan to get started',
      description: "To unlock your full dashboard and let our team start work, you'll need to choose a plan. You can do this from the 'Choose a plan' banner or the Billing & Plan section (we'll point it out shortly).",
    },
  },
  {
    el: '[data-testid="nav-growth-opportunity-snapshot"]',
    popover: { title: 'Growth Opportunity Snapshot', description: 'Your AI-powered summary of where your firm can grow across search and AI visibility.' },
  },
  {
    el: '[data-testid="nav-overview"]',
    popover: { title: 'Overview', description: 'A high-level view of your firm\'s performance - citations, rankings and organic growth at a glance.' },
  },
  {
    el: '[data-testid="nav-keywords-&-queries"]',
    popover: { title: 'Keywords & Queries', description: 'Track the keywords and AI prompts your firm is being found for.' },
  },
  {
    el: '[data-testid="nav-position-tracking"]',
    popover: { title: 'Position Tracking', description: 'Monitor your rankings across search engines and AI assistants over time.' },
  },
  {
    el: '[data-testid="nav-traffic-analytics"]',
    popover: { title: 'Traffic Analytics', description: 'See how visitors are finding and engaging with your website.' },
  },
  {
    el: '[data-testid="nav-email-marketing"]',
    popover: { title: 'Email Marketing', description: 'Manage campaigns and see how your email outreach is performing.' },
  },
  {
    el: '[data-testid="nav-reporting"]',
    popover: { title: 'Reporting', description: 'Access your performance reports whenever you need them - no waiting for month-end.' },
  },
  {
    el: '[data-testid="sidebar-addons-section"]',
    popover: { title: 'Add-ons', description: 'Extra services like Social Media, Paid Ads, AI Video, Website Creation and Product Development. Open any tab to explore - you only subscribe when you submit.' },
  },
  {
    el: '[data-testid="nav-billing-plan"]',
    popover: { title: 'Billing & Plan', description: 'Manage your subscription, change your plan or update payment details here.' },
  },
  {
    el: '[data-testid="messages-bubble"]',
    popover: { title: 'Chat with your team', description: 'Message the specialists working on your account directly - no scattered email threads.', side: 'left', align: 'end' },
  },
  {
    popover: {
      title: "You're all set! 🎉",
      description: 'Explore at your own pace. Need a refresher? Click the "Take a tour" button (bottom-right) anytime.',
    },
  },
];

function buildSteps() {
  return STEP_DEFS
    .filter((s) => !s.el || document.querySelector(s.el))
    .map((s) => (s.el ? { element: s.el, popover: s.popover } : { popover: s.popover }));
}

function startTour(onDone) {
  const steps = buildSteps();
  if (!steps.length) return;
  const d = driver({
    showProgress: true,
    allowClose: true,
    overlayColor: 'rgba(15, 23, 42, 0.75)',
    showButtons: ['next', 'previous', 'close'],
    nextBtnText: 'Next',
    prevBtnText: 'Back',
    doneBtnText: 'Done',
    progressText: '{{current}} of {{total}}',
    steps,
    onDestroyed: () => { if (onDone) onDone(); },
  });
  d.drive();
}

export default function DashboardTour({ user, enabled = true }) {
  const startedRef = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    // Auto-start once per user (clients only) after the sidebar has rendered.
    if (user?.role !== 'client') return;
    let seen = false;
    try { seen = localStorage.getItem(TOUR_SEEN_KEY) === '1'; } catch (_e) { seen = false; }
    if (seen || startedRef.current) return;
    // Don't collide with the onboarding video, which pops first for new users.
    let videoSeen = false;
    try { videoSeen = localStorage.getItem('la360_onboarding_video_v1') === '1'; } catch (_e) { videoSeen = false; }
    if (!videoSeen) return;
    startedRef.current = true;
    const t = setTimeout(() => {
      startTour(() => { try { localStorage.setItem(TOUR_SEEN_KEY, '1'); } catch (_e) { /* ignore */ } });
    }, 900);
    return () => clearTimeout(t);
  }, [user, enabled]);

  if (!enabled) return null;

  return (
    <button
      type="button"
      onClick={() => startTour(() => { try { localStorage.setItem(TOUR_SEEN_KEY, '1'); } catch (_e) { /* ignore */ } })}
      className="fixed bottom-24 right-6 z-40 inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/90 backdrop-blur-sm border border-slate-200 shadow-lg shadow-black/10 text-slate-700 text-sm font-semibold hover:bg-white hover:text-violet-700 transition-all"
      data-testid="take-a-tour-btn"
    >
      <HelpCircle className="w-4 h-4" />
      Take a tour
    </button>
  );
}
